import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';

interface Segment {
  id: string;
  code: string;
  name: string;
  active: boolean;
  document_type: 'DNI' | 'RUC';
  created_at: string;
}

const Segments: React.FC = () => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    active: true,
    document_type: 'DNI' as 'DNI' | 'RUC'
  });

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/segments');
      setSegments(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar segmentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (editingSegment) {
        await api.put(`/segments/${editingSegment.id}`, formData);
      } else {
        await api.post('/segments', formData);
      }

      await loadSegments();
      setShowModal(false);
      setFormData({ code: '', name: '', active: true, document_type: 'DNI' });
      setEditingSegment(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar segmento');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (segment: Segment) => {
    setEditingSegment(segment);
    setFormData({
      code: segment.code,
      name: segment.name,
      active: segment.active,
      document_type: segment.document_type
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar este segmento?')) {
      try {
        setLoading(true);
        await api.delete(`/segments/${id}`);
        await loadSegments();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al eliminar segmento');
      } finally {
        setLoading(false);
      }
    }
  };

  const openNewModal = () => {
    setEditingSegment(null);
    setFormData({ code: '', name: '', active: true, document_type: 'DNI' });
    setShowModal(true);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Gestión de Segmentos</h2>
            <button 
              className="btn btn-primary"
              onClick={openNewModal}
            >
              <FaPlus className="me-2" />
              Nuevo Segmento
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {loading && (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          )}

          <div className="row">
            {segments.map((segment) => (
              <div key={segment.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{segment.name}</h5>
                    <span className={`badge ${segment.active ? 'bg-success' : 'bg-secondary'}`}>
                      {segment.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="card-body">
                    <p><strong>Código:</strong> {segment.code}</p>
                    <p><strong>Tipo de Documento:</strong> {segment.document_type}</p>
                    <p><strong>Creado:</strong> {new Date(segment.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="card-footer">
                    <div className="btn-group w-100" role="group">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleEdit(segment)}
                      >
                        <FaEdit className="me-1" />
                        Editar
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(segment.id)}
                      >
                        <FaTrash className="me-1" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal para crear/editar segmento */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingSegment ? 'Editar Segmento' : 'Nuevo Segmento'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Código <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      required
                      disabled={!!editingSegment}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Nombre <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Tipo de Documento <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={formData.document_type}
                      onChange={(e) => setFormData({ ...formData, document_type: e.target.value as 'DNI' | 'RUC' })}
                      required
                    >
                      <option value="DNI">DNI</option>
                      <option value="RUC">RUC</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      />
                      <label className="form-check-label">
                        Activo
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : editingSegment ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Segments;